import { KIND, SHAPE } from 'baseui/button'
import { CheckIndeterminate, Plus } from 'baseui/icon'
import { Slider } from 'baseui/slider'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import Icons from '@/scenes/Editor/components/Icons'
import { Z_INDEX } from '@/constants/z-index'
import { lightTheme } from '@/customTheme'

type ZoomControlsProps = {
  value: number
  min: number
  max: number
  step: number
  disabled?: boolean
  showReset?: boolean
  trackPercent?: number
  onReset: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  onChange: (value: number) => void
}

function ZoomControls({
  value,
  min,
  max,
  step,
  disabled = false,
  showReset = false,
  trackPercent: trackPercentOverride,
  onReset,
  onZoomOut,
  onZoomIn,
  onChange,
}: ZoomControlsProps) {
  const calculatedTrackPercent = max === min
    ? 0
    : Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100)
  const trackPercent = trackPercentOverride ?? calculatedTrackPercent

  return (
    <div
      style={{
        position: 'relative',
        zIndex: Z_INDEX.MAIN_ZOOM_CONTROLS,
        boxSizing: 'border-box',
        background: '#fff',
        height: '40px',
        display: 'flex',
        gap: '8px',
        borderRadius: '8px',
        padding: '4px',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onReset}
        tooltip={{ content: 'Reset zoom', placement: 'top' }}
        style={{
          display: 'flex',
          borderRadius: '50%',
          visibility: showReset ? 'visible' : 'hidden',
          opacity: 1,
          pointerEvents: disabled || !showReset ? 'none' : 'auto',
        }}
      >
        <Icons.ResetZoomIcon disabled={disabled} />
      </ButtonCustom>
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onZoomOut}
        tooltip={{ content: 'Zoom out', placement: 'top' }}
        style={{
          pointerEvents: disabled ? 'none' : 'auto',
          borderRadius: '50%',
        }}
      >
        <CheckIndeterminate color={disabled ? lightTheme.colors.icon.disable : lightTheme.colors.blackGray} size={24} title="" />
      </ButtonCustom>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '72px',
          margin: '0 4px',
          alignItems: 'center',
        }}
      >
        <Slider
          value={[value]}
          min={min}
          max={max}
          step={step}
          onChange={({ value: nextValue }) => nextValue && onChange(nextValue[0])}
          overrides={{
            Track: { style: () => ({ paddingLeft: '0', paddingRight: '0' }) },
            InnerThumb: () => <></>,
            ThumbValue: () => null,
            Thumb: {
              style: () => ({
                height: '20px',
                width: '20px',
                borderRadius: '50%',
                boxShadow: '0px 0px 6px rgba(0,0,0,.3)',
                backgroundColor: '#fff',
              }),
            },
            InnerTrack: {
              style: () => ({
                background: `linear-gradient(to right, ${lightTheme.colors.blackGray} 0%, ${lightTheme.colors.grayScale600} ${trackPercent}%, ${lightTheme.colors.grayScale100} ${trackPercent}%, ${lightTheme.colors.grayScale100} 100%)`,
                width: '100%',
              }),
            },
            Tick: { style: () => ({ display: 'none' }) },
            TickBar: { style: () => ({ display: 'none' }) },
          }}
          disabled={disabled}
        />
      </div>
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onZoomIn}
        tooltip={{ content: 'Zoom in', placement: 'top' }}
        style={{
          pointerEvents: disabled ? 'none' : 'auto',
          borderRadius: '50%',
        }}
      >
        <Plus color={disabled ? lightTheme.colors.icon.disable : lightTheme.colors.blackGray} size={24} title="" />
      </ButtonCustom>
    </div>
  )
}

export default ZoomControls
